import { useEffect, useState } from "react";
import { useBrandSources, BrontoIntelligence } from "@/components/Brand";

/**
 * WelcomeSplash — Apple-level intro shown after successful login/signup.
 *
 * Now branded with the authoritative iOS `welcome.png` / `welcome-dark.png`
 * cursive rainbow lockup (uploaded by the app owner).  The PNG reveals
 * left-to-right via a clip-path sweep, so the entire cursive greeting is
 * drawn out like slow handwriting.  Below it, the Bronto Intelligence
 * lockup fades in, then the whole overlay blurs out.
 *
 * Total runtime ≈ 4.3s:
 *   0.00s  overlay fades in
 *   0.35s  handwriting reveal starts (2.6s duration)
 *   3.00s  Bronto Intelligence lockup fades up
 *   3.60s  hold
 *   3.60s→4.30s  overlay fades + blurs out, `onFinish()` fires at 4.30s
 *
 * `text` is accepted for backwards-compat but ignored — the branded PNG
 * always reads "Welcome" (the iOS asset).
 */
export default function WelcomeSplash({ text: _text, onFinish }) {
  const { welcome } = useBrandSources();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const startLeave = setTimeout(() => setLeaving(true), 3600);
    const finish = setTimeout(() => onFinish?.(), 4300);
    return () => {
      clearTimeout(startLeave);
      clearTimeout(finish);
    };
  }, [onFinish]);

  return (
    <div
      className={`welcome-splash-overlay fixed inset-0 z-[100] flex items-center justify-center bg-background ${
        leaving ? "is-leaving" : ""
      }`}
      role="status"
      aria-live="polite"
      data-testid="welcome-splash"
    >
      <div className="welcome-glow absolute inset-0 pointer-events-none" />
      <div className="y-pattern absolute inset-0 opacity-40 pointer-events-none" />

      <div className="relative flex flex-col items-center px-8 text-center">
        {/* Branded cursive rainbow "Welcome" — hand-drawn reveal via clip-path.
            Aspect ratio comes from the trimmed source PNG dimensions. */}
        <div className="welcome-cursive inline-block" data-testid="welcome-splash-heading">
          <img
            src={welcome}
            alt="Welcome"
            draggable={false}
            className="block select-none"
            style={{
              width: "min(80vw, 720px)",
              height: "auto",
              maxHeight: "40vh",
              objectFit: "contain",
            }}
          />
        </div>

        <div className="welcome-subtitle mt-8 opacity-0">
          <BrontoIntelligence markHeight={40} wordmarkHeight={16} />
        </div>
      </div>
    </div>
  );
}
